import Cookies from "js-cookie";
import * as utf8 from "utf8";
import { reportError } from "./datadog";
import { isDodgyPluginError, isReportableError, SELF_REPORTED_ERROR } from "./reportableErrors";

type ErrorArgs = Record<string, unknown> | unknown[];

// Acceptance tests read this cookie to fail a scenario when a JS error occurs.
const ERROR_COOKIE = "javascript-error";
// An empty path makes the browser default it to the current directory, as $.cookie did.
const ERROR_COOKIE_ATTRIBUTES = { path: "" };

export const getExceptionInCookie = () => Cookies.get(ERROR_COOKIE);

export const clearExceptionInCookie = () => {
  Cookies.remove(ERROR_COOKIE, ERROR_COOKIE_ATTRIBUTES);
};

const storeErrorInCookie = (str: string) => {
  const existing = getExceptionInCookie();
  const value = existing ? `${utf8.decode(atob(existing))}\n${str}` : str;
  Cookies.set(ERROR_COOKIE, btoa(utf8.encode(value)), ERROR_COOKIE_ATTRIBUTES);
};

const showErrorInPopup = (message: string) => {
  const { bootbox } = window as { bootbox?: { headed_dialog: (t: string, b: string) => void } };
  bootbox?.headed_dialog("Error", message);
};

// Our TS code marks errors it has already shown to the user (see displayErrorInModal).
const wasDisplayedToUser = (error: unknown) =>
  typeof error === "object" && error !== null
    && !!(error as { __displayedToUser?: boolean }).__displayedToUser;

const messageOf = (error: unknown) => {
  const nested = (error as { error?: { message?: unknown } } | null)?.error?.message;
  if (nested) { return `${nested}`; }
  if (error instanceof Error) { return error.message; }
  return `${error}`;
};

const MAX_ARGS_LENGTH = 500;

// Args can be circular (e.g. ajax options).
const describeArgs = (args: ErrorArgs) => {
  let description;
  try {
    description = JSON.stringify(args);
  } catch {
    description = `${args}`;
  }
  return description.slice(0, MAX_ARGS_LENGTH);
};

const logError = (message: string, displayError: boolean, args: ErrorArgs) => {
  if (window.env !== "production") {
    storeErrorInCookie([message, displayError, describeArgs(args)].join(", "));
  }
  if (window.env !== "test") {
    // eslint-disable-next-line no-console
    console.log(message, args);
  }
};

/**
 * With all errors we:
 * 1) Send to Datadog (unless it was uncaught, which Datadog collects itself)
 * 2) Store in a cookie for acceptance tests (non-production only)
 * 3) Log to the console
 * 4) Send to session replay
 * 5) Show in popup (optionally)
 */
export const handleError = (
  error: unknown,
  displayError: boolean,
  args: ErrorArgs = {},
  { report = true } = {}
) => {
  const message = messageOf(error);
  // Perform all of this in a /dev/null try/catch, so there's no tight loop if it goes wrong.
  try {
    if (isDodgyPluginError(message)) { return; }

    if (report) { reportError(message, error); }
    logError(message, displayError, args);
    window.SessionReplaySupport?.sendError(message);

    if (displayError && !wasDisplayedToUser(error)) {
      showErrorInPopup(message);
    }
  } catch {
    // Swallow errors from error handling.
  }
};

/**
 * For when we know an error has happened. Handles the error, then throws it so the calling code
 * stops executing.
 */
export const raiseError = (message: string, ...args: unknown[]): never => {
  // Not reported here: Datadog collects the thrown error if it's uncaught.
  handleError(SELF_REPORTED_ERROR + message, true, args, { report: false });
  throw new Error(message);
};

export const handleUncaughtError = (event: ErrorEvent) => {
  const { message, filename, lineno, error } = event;
  const stack = (error as Error | undefined)?.stack;

  if (isReportableError(message, filename, lineno, stack)) {
    // Uncaught errors are collected by Datadog directly.
    handleError(message, true, { url: filename, line: lineno, stack }, { report: false });
  } else if (window.env !== "test") {
    // eslint-disable-next-line no-console
    console.log("Unreportable error", message, filename, lineno, event.colno, error);
  }
};

export const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
  handleError(event.reason, true, {}, { report: false });
};

export const installGlobalErrorHandlers = () => {
  // Listeners rather than window.onerror, which Datadog wraps to collect uncaught errors.
  window.addEventListener("error", handleUncaughtError);
  window.addEventListener("unhandledrejection", handleUnhandledRejection);
};
